import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import surveyService from '../../../services/survey.service';
import './Surveys.css';
import './SurveyResultPage.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const RESPONSE_STATUS_META = {
    COMPLETED: { label: 'Completed', className: 'is-completed', icon: 'bi-check2-circle' },
    PARTIAL: { label: 'Partial', className: 'is-partial', icon: 'bi-dash-circle' },
    ABANDONED: { label: 'Abandoned', className: 'is-abandoned', icon: 'bi-x-circle' },
};

const CHANNEL_META = {
    LINK: { label: 'Link', icon: 'bi-link-45deg' },
    EMAIL: { label: 'Email', icon: 'bi-envelope' },
    SMS: { label: 'SMS', icon: 'bi-chat-dots' },
};

const STATUS_FILTERS = [
    { value: '', label: 'All statuses' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'PARTIAL', label: 'Partial' },
    { value: 'ABANDONED', label: 'Abandoned' },
];

const formatCount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';
    return number.toLocaleString();
};

const formatDuration = (seconds) => {
    const value = Number(seconds);
    if (!Number.isFinite(value) || value <= 0) return '—';
    if (value < 60) return `${Math.round(value)}s`;
    const mins = Math.floor(value / 60);
    const secs = Math.round(value % 60);
    return secs ? `${mins}m ${secs}s` : `${mins}m`;
};

const formatAnswer = (answer) => {
    if (!answer) return '—';
    if (answer.answerText) return answer.answerText;
    if (answer.answerNumeric != null && answer.answerNumeric !== '') {
        return String(answer.answerNumeric);
    }
    if (answer.answerValue) {
        try {
            const parsed = JSON.parse(answer.answerValue);
            if (Array.isArray(parsed)) return parsed.join(', ');
        } catch {
            // plain string
        }
        return answer.answerValue;
    }
    return '—';
};

function SurveyResultPage() {
    const { surveyId } = useParams();
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [survey, setSurvey] = useState(null);
    const [stats, setStats] = useState(null);
    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState(null);

    const loadSummary = useCallback(async () => {
        if (!clinicId || !surveyId) return;

        try {
            const [surveyRes, statsRes] = await Promise.all([
                surveyService.getClinicSurveyById(clinicId, surveyId),
                surveyService.getClinicSurveyResultStats(clinicId, surveyId).catch(() => null),
            ]);
            setSurvey(surveyRes.data?.survey || null);
            setStats(statsRes?.data || null);
        } catch (error) {
            console.error('Failed to load survey summary', error);
            toast.error(error?.response?.data?.message || 'Could not load survey results.');
            setSurvey(null);
            setStats(null);
        }
    }, [clinicId, surveyId]);

    const loadResponses = useCallback(
        async (currentPage, currentSize, search, status) => {
            if (!clinicId || !surveyId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const responsesRes = await surveyService.getClinicSurveyResponses(
                    clinicId,
                    surveyId,
                    currentPage,
                    currentSize,
                    {
                        sortBy: 'submittedAt',
                        sortOrder: 'DESC',
                        searchTerm: search,
                        statusFilter: status,
                    }
                );

                const pageData = responsesRes.data || {};
                setResponses(pageData.content || []);
                setTotalElements(pageData.totalElements || 0);
                setTotalPages(pageData.totalPages || 0);
            } catch (error) {
                console.error('Failed to load survey responses', error);
                toast.error(error?.response?.data?.message || 'Could not load survey responses.');
                setResponses([]);
                setTotalElements(0);
                setTotalPages(0);
            } finally {
                setLoading(false);
            }
        },
        [clinicId, surveyId]
    );

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        loadResponses(pageNo, pageSize, appliedSearch, statusFilter);
    }, [loadResponses, pageNo, pageSize, appliedSearch, statusFilter]);

    const statCards = useMemo(
        () => [
            { key: 'total', label: 'Total responses', value: stats?.total, icon: 'bi-collection' },
            { key: 'completed', label: 'Completed', value: stats?.completed, icon: 'bi-check2-circle' },
            { key: 'partial', label: 'Partial', value: stats?.partial, icon: 'bi-dash-circle' },
            { key: 'abandoned', label: 'Abandoned', value: stats?.abandoned, icon: 'bi-x-circle' },
            { key: 'link', label: 'Via link', value: stats?.linkChannel, icon: 'bi-link-45deg' },
            {
                key: 'avg',
                label: 'Avg duration',
                value: formatDuration(stats?.avgDurationSeconds),
                icon: 'bi-stopwatch',
                raw: true,
            },
        ],
        [stats]
    );

    const handleSearch = (event) => {
        event.preventDefault();
        setPageNo(0);
        setAppliedSearch(searchTerm.trim());
    };

    const openDetail = async (responseId) => {
        if (!clinicId || !surveyId || !responseId) return;
        setDetailOpen(true);
        setDetailLoading(true);
        setDetail(null);
        try {
            const res = await surveyService.getClinicSurveyResponseById(clinicId, surveyId, responseId);
            setDetail(res.data || null);
        } catch (error) {
            console.error('Failed to load survey response detail', error);
            toast.error(error?.response?.data?.message || 'Could not load response details.');
            setDetailOpen(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setDetailOpen(false);
        setDetail(null);
    };

    return (
        <div className="ecx-page surveys-page survey-results-page ecx-page--full-width">
            <SubHeader
                title={survey?.title || 'Survey results'}
                description="Individual submissions and summary stats for this survey"
                icon="bi-bar-chart-line"
                containerClass="w-100 px-4"
            >
                <div className="d-flex gap-2">
                    <Link
                        to={`/surveys/${surveyId}/graphs`}
                        className="btn btn-sm btn-primary rounded-pill"
                    >
                        <i className="bi bi-bar-chart-line me-1" />
                        Graphs
                    </Link>
                    <Link
                        to={`/surveys/${surveyId}/edit`}
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                    >
                        <i className="bi bi-pencil me-1" />
                        Edit survey
                    </Link>
                    <Link to="/surveys" className="btn btn-sm btn-outline-secondary rounded-pill">
                        <i className="bi bi-chevron-left me-1" />
                        Back
                    </Link>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                {loading && !survey ? (
                    <div className="ecx-page-card ecx-page-panel">
                        <div className="ecx-page-panel__body py-5 text-center text-muted">
                            Loading survey results…
                        </div>
                    </div>
                ) : !survey ? (
                    <div className="ecx-page-card ecx-page-panel">
                        <div className="ecx-page-panel__body py-5 text-center">
                            <h2 className="h5 mb-2">Survey not found</h2>
                            <p className="text-muted mb-0">
                                This survey may have been deleted or is unavailable.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="survey-results__summary mb-3">
                            <div className="survey-results__summary-main">
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                    <span
                                        className={`surveys-status ${
                                            survey.status
                                                ? `surveys-status--${String(survey.status).toLowerCase()}`
                                                : ''
                                        }`}
                                    >
                                        {String(survey.status || '—')
                                            .toLowerCase()
                                            .replace(/_/g, ' ')
                                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                                    </span>
                                    {(stats?.targetResponse != null || stats?.maxResponse != null) && (
                                        <span className="ecx-page-chip">
                                            Target {stats?.targetResponse ?? '—'} / Max {stats?.maxResponse ?? '—'}
                                        </span>
                                    )}
                                </div>
                                {survey.description ? (
                                    <p className="survey-results__description mb-0">{survey.description}</p>
                                ) : (
                                    <p className="survey-results__description mb-0 text-muted">
                                        No description provided for this survey.
                                    </p>
                                )}
                            </div>

                            <div className="survey-results__stat-grid">
                                {statCards.map((card) => (
                                    <div key={card.key} className="survey-results__stat-card">
                                        <div className="survey-results__stat-label">
                                            <i className={`bi ${card.icon}`} aria-hidden="true" />
                                            {card.label}
                                        </div>
                                        <div className="survey-results__stat-value">
                                            {card.raw ? card.value : formatCount(card.value)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ecx-page-card ecx-page-panel surveys-panel">
                            <div className="ecx-page-panel__header">
                                <div className="survey-results__toolbar">
                                    <div>
                                        <h2 className="h6 mb-1">Responses</h2>
                                        <p className="text-muted small mb-0">
                                            {formatCount(totalElements)} submission
                                            {totalElements === 1 ? '' : 's'}
                                        </p>
                                    </div>
                                    <form className="survey-results__filters" onSubmit={handleSearch}>
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text bg-white">
                                                <i className="bi bi-search text-muted" />
                                            </span>
                                            <input
                                                type="search"
                                                className="form-control"
                                                placeholder="Search patient or email"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                        <select
                                            className="form-select form-select-sm"
                                            value={statusFilter}
                                            onChange={(e) => {
                                                setPageNo(0);
                                                setStatusFilter(e.target.value);
                                            }}
                                        >
                                            {STATUS_FILTERS.map((option) => (
                                                <option key={option.value || 'all'} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <button type="submit" className="btn btn-sm btn-primary rounded-pill">
                                            Search
                                        </button>
                                    </form>
                                </div>
                            </div>

                            <div className="ecx-page-panel__body">
                                {loading ? (
                                    <div className="py-5 text-center text-muted">Loading responses…</div>
                                ) : responses.length === 0 ? (
                                    <div className="py-5 text-center">
                                        <div className="mb-3">
                                            <i className="bi bi-inbox fs-1 text-muted" />
                                        </div>
                                        <h5 className="mb-2 text-dark">No responses yet</h5>
                                        <p className="text-muted mb-0 mx-auto" style={{ maxWidth: '28rem' }}>
                                            When patients complete this survey, their submissions will appear here.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive survey-results__table-wrap">
                                            <table className="table table-hover align-middle mb-0 survey-results__table">
                                                <thead>
                                                    <tr>
                                                        <th>Patient</th>
                                                        <th>Contact</th>
                                                        <th>Status</th>
                                                        <th>Channel</th>
                                                        <th>Progress</th>
                                                        <th>Duration</th>
                                                        <th>Submitted</th>
                                                        <th className="text-end">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {responses.map((row) => {
                                                        const statusMeta =
                                                            RESPONSE_STATUS_META[row.status] || {
                                                                label: row.status || 'Unknown',
                                                                className: 'is-unknown',
                                                                icon: 'bi-question-circle',
                                                            };
                                                        const channelMeta =
                                                            CHANNEL_META[row.channel] || {
                                                                label: row.channel || '—',
                                                                icon: 'bi-broadcast',
                                                            };
                                                        const email = row.patientEmail || row.contactEmail || '—';
                                                        const phone = row.patientPhone || row.contactPhone || '';

                                                        return (
                                                            <tr key={row.id}>
                                                                <td>
                                                                    <div className="fw-semibold">
                                                                        {row.patientName || 'Anonymous'}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="small">{email}</div>
                                                                    {phone ? (
                                                                        <div className="small text-muted">{phone}</div>
                                                                    ) : null}
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        className={`survey-results__status ${statusMeta.className}`}
                                                                    >
                                                                        <i className={`bi ${statusMeta.icon}`} />
                                                                        {statusMeta.label}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="small text-muted">
                                                                        <i className={`bi ${channelMeta.icon} me-1`} />
                                                                        {channelMeta.label}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {row.answeredCount ?? 0}/
                                                                    {row.totalQuestions ?? 0}
                                                                </td>
                                                                <td>{formatDuration(row.durationSeconds)}</td>
                                                                <td className="small text-muted">
                                                                    {row.submittedAt || row.createdAt || '—'}
                                                                </td>
                                                                <td className="text-end">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary rounded-pill"
                                                                        onClick={() => openDetail(row.id)}
                                                                    >
                                                                        View
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {totalPages > 0 ? (
                                            <div className="ecx-page-pagination border-top p-3">
                                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100">
                                                    <label className="d-inline-flex align-items-center gap-2 small text-muted mb-0">
                                                        Rows
                                                        <select
                                                            className="form-select form-select-sm"
                                                            style={{ width: 'auto' }}
                                                            value={pageSize}
                                                            onChange={(e) => {
                                                                setPageNo(0);
                                                                setPageSize(Number(e.target.value));
                                                            }}
                                                        >
                                                            {PAGE_SIZE_OPTIONS.map((size) => (
                                                                <option key={size} value={size}>
                                                                    {size}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                    <Pagination
                                                        currentPage={pageNo}
                                                        totalPages={totalPages}
                                                        totalElements={totalElements}
                                                        pageSize={pageSize}
                                                        onPageChange={setPageNo}
                                                    />
                                                </div>
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {detailOpen ? (
                <div className="survey-results__drawer-backdrop" onClick={closeDetail} role="presentation">
                    <aside
                        className="survey-results__drawer"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Survey response details"
                    >
                        <div className="survey-results__drawer-header">
                            <div>
                                <h2 className="h5 mb-1">Response details</h2>
                                <p className="text-muted small mb-0">
                                    {detail?.response?.patientName || 'Anonymous patient'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light rounded-circle"
                                onClick={closeDetail}
                                aria-label="Close"
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>

                        <div className="survey-results__drawer-body">
                            {detailLoading ? (
                                <div className="py-4 text-center text-muted">Loading answers…</div>
                            ) : !detail?.response ? (
                                <div className="py-4 text-center text-muted">Response not found.</div>
                            ) : (
                                <>
                                    <div className="survey-results__detail-meta">
                                        <div>
                                            <span className="survey-results__meta-label">Submitted</span>
                                            <div>{detail.response.submittedAt || '—'}</div>
                                        </div>
                                        <div>
                                            <span className="survey-results__meta-label">Status</span>
                                            <div>{detail.response.status || '—'}</div>
                                        </div>
                                        <div>
                                            <span className="survey-results__meta-label">Channel</span>
                                            <div>{detail.response.channel || '—'}</div>
                                        </div>
                                        <div>
                                            <span className="survey-results__meta-label">Duration</span>
                                            <div>{formatDuration(detail.response.durationSeconds)}</div>
                                        </div>
                                    </div>

                                    <div className="survey-results__answers">
                                        {(detail.answers || []).length === 0 ? (
                                            <p className="text-muted mb-0">No answers recorded for this response.</p>
                                        ) : (
                                            detail.answers.map((answer, index) => (
                                                <div key={answer.id || index} className="survey-results__answer">
                                                    <div className="survey-results__answer-title">
                                                        <span className="text-muted me-1">{index + 1}.</span>
                                                        {answer.questionTitle || answer.questionName || 'Question'}
                                                    </div>
                                                    <div className="survey-results__answer-type text-muted">
                                                        {answer.questionType || 'unknown'}
                                                    </div>
                                                    <div className="survey-results__answer-value">
                                                        {formatAnswer(answer)}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </aside>
                </div>
            ) : null}
        </div>
    );
}

export default SurveyResultPage;
